import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import i18n from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Translation resources
import en from '../locales/en.json';
import sw from '../locales/sw.json';

// Initialize i18next
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      sw: { translation: sw }
    },
    fallbackLng: 'en',
    debug: process.env.NODE_ENV === 'development',
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage']
    },
    interpolation: {
      escapeValue: false
    }
  });

export type Language = 'en' | 'sw';

interface LanguageContextType {
  currentLanguage: Language;
  changeLanguage: (language: Language) => Promise<void>;
  t: (key: string, options?: any) => string;
  isRTL: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState<Language>('en');
  const [isRTL, setIsRTL] = useState(false);
  const { t: translate } = useTranslation();

  useEffect(() => {
    const detectedLanguage = i18n.language as Language;
    setCurrentLanguage(detectedLanguage);
    
    // Update RTL for Swahili (although Swahili is LTR, this structure allows for future RTL languages)
    setIsRTL(detectedLanguage === 'sw' ? false : false);
    
    // Update document language and direction
    document.documentElement.lang = detectedLanguage;
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
  }, [isRTL]);

  const changeLanguage = async (language: Language): Promise<void> => {
    try {
      await i18n.changeLanguage(language);
      setCurrentLanguage(language);
      
      // Update RTL setting
      const newIsRTL = language === 'sw' ? false : false; // Swahili is LTR
      setIsRTL(newIsRTL);
      
      // Update document attributes
      document.documentElement.lang = language;
      document.documentElement.dir = newIsRTL ? 'rtl' : 'ltr';
      
      // Store in localStorage
      localStorage.setItem('i18nextLng', language);
      
      // Reload page to ensure all components use new language
      window.location.reload();
    } catch (error) {
      console.error('Failed to change language:', error);
    }
  };

  const t = (key: string, options?: any): string => {
    const result = translate(key, options);
    return typeof result === 'string' ? result : JSON.stringify(result);
  };

  const value: LanguageContextType = {
    currentLanguage,
    changeLanguage,
    t,
    isRTL
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default i18n;